import { useIsClientOrTeamOrFreeTrial } from '@upright/ui/common/generic/hooks';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { useLocation } from 'react-router-dom';
import { clientInputRoutes } from './route-declarations/client-input-routes';
import { homeViewRoutes } from './route-declarations/home-view-routes';

/**
 * Whether the signed-in user gets the logged-in home at `/` and the home view routes, rather than
 * the public lander: a client, a free-trial user or the team, as the groups say, or a customer the
 * unified shell is on for.
 *
 * The second half is there because the shell's audience follows the organisation type (PLT-8105),
 * so an organisation in no client group can be in the shell, and the group test alone would hand
 * its users the public lander inside the shell's frame. While a customer's opt-in resolves it
 * answers yes, and the home view switch renders nothing meanwhile, so the lander is not painted
 * and swapped.
 *
 * The route tree and `useUnifiedShellFrameActive` both read it, so the frame and the routes it
 * wraps cannot disagree.
 */
export function useShowLoggedInHomeView(): boolean {
  const isClientOrTeamOrFreeTrial = useIsClientOrTeamOrFreeTrial();
  const shell = useUnifiedShell();
  return isClientOrTeamOrFreeTrial || shell.enabled || shell.loading;
}

/**
 * Home view is only shown for
 * - logged in users
 * - who are either on the '/' route or
 * - or some other route specified in homeViewPaths (see below)
 */

export function useShouldShowHomeViewFrame(loggedInHomeViewEnabled: boolean) {
  const location = useLocation();
  if (!loggedInHomeViewEnabled) return false;

  const currentPath = location.pathname.split('/')[1];
  const isOnLander = currentPath.length === 0;
  const isOnHomeViewPath = homeViewPaths.some(
    homeViewPath => currentPath.slice(0, homeViewPath.length) === homeViewPath.split('/')[0],
  );

  return isOnLander || isOnHomeViewPath;
}

/**
 * In addition to homeViewPaths, home view frame is shown also for clientInputRoutes. They are declared separately because they can also be
 * shown without the home view frame for non-logged-in users who have a secret link.
 */
const homeViewPaths = [...homeViewRoutes.map(r => r.path), ...clientInputRoutes.map(r => r.path)];
